import { plainToClass } from "class-transformer";
import AuthorizationService from "../services/authorizationService";
import RepositoryBase from "./repositoryBase";
import Omission from "models/omission";
import EshopError from "models/eshopError";
import EventBus from "../services/eventBus";
import EshopRequestBuilder from "../data/eshopRequestBuilder";
import { toJS } from "mobx";
import IsdsResponse from "models/isdsResponse";
import { PersistedBase64 } from "models/notice";
import { parseFileNameFromHeaderResponse, persistedBlobToISDSFormat } from "../utils/fileUtils";
import ExemptionCancellationApplicationProductionResponse from "../entities/exemptionCancellationApplicationProductionResponse";
import BlobWithFileName from "models/blobWithFileName";

export const GET_EXEMPTION_REQUEST_FAILURE = "GET_EXEMPTION_REQUEST_FAILURE";
export const POST_OMISSION_REQUEST_FAILURE = "POST_OMISSION_REQUEST_FAILURE";
export const POST_OMISSION_PREVIEW_REQUEST_FAILURE = "POST_OMISSION_PREVIEW_REQUEST_FAILURE";
const POST_OMISSION_APPLICATIONS_DOCUMENTS_ISDS_FAILURE = "POST_OMISSION_APPLICATIONS_DOCUMENTS_ISDS_FAILURE";

export default class OmissionRepository extends RepositoryBase<EshopRequestBuilder> {
  constructor(
    protected apiFactory: ReturnType<typeof EshopRequestBuilder.Factory>,
    protected authorizationService: AuthorizationService,
    protected eventBus: EventBus
  ) {
    super(() => apiFactory(), authorizationService, eventBus);
  }

  createOmissionRequestObj(omission: Omission) {
    const notice = toJS(omission);

    if (notice.vehicleOperator) {
      if (notice.vehicleOperator.isLegalPerson) {
        notice.vehicleOperator.naturalPerson = undefined;
      } else {
        notice.vehicleOperator.legalPerson = undefined;
      }
    }

    if (
      notice.vehicleOperator?.isLegalPerson === false &&
      notice.vehicleOperator?.naturalPerson?.isRepresentedByProxy === false
    ) {
      notice.vehicleOperator.naturalPerson = undefined;
    }

    if (
      (!notice.vehicleOperator?.legalPerson?.companyAddress ||
        !notice.vehicleOperator?.legalPerson?.companyRegistrationNumber ||
        !notice.vehicleOperator?.legalPerson?.companyName) &&
      (!notice.vehicleOperator?.naturalPerson?.mailingAddress ||
        !notice.vehicleOperator?.naturalPerson?.birthDate ||
        !notice.vehicleOperator?.naturalPerson?.fullName)
    ) {
      notice.vehicleOperator = undefined;
    }

    return notice;
  }

  getProduction(omission: Omission): Promise<EshopError | ExemptionCancellationApplicationProductionResponse> {
    const notice = this.createOmissionRequestObj(omission);

    return this.callApi(() =>
      this.apiFactory()
        .apiVersion(3)
        .one("exemptions/cancellation_application_preview")
        .post(notice)
        .then(response => {
          return plainToClass(ExemptionCancellationApplicationProductionResponse, response);
        })
        .catch(() => new EshopError(POST_OMISSION_PREVIEW_REQUEST_FAILURE))
    );
  }

  createOmission(omission: Omission): Promise<EshopError | BlobWithFileName> {
    const notice = this.createOmissionRequestObj(omission);

    return this.callApi(api =>
      api
        .apiVersion(3)
        .one("exemptions/cancellation_applications")
        .clearPost(notice, undefined, undefined, undefined, "application/pdf")
        .then(async (response: any) => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          const fileName = parseFileNameFromHeaderResponse(response.headers.get("Content-Disposition"));
          const blob = await response.blob();

          return { blob: blob, fileName: fileName ?? "download" } as BlobWithFileName;
        })
        .catch(() => {
          throw new EshopError(POST_OMISSION_REQUEST_FAILURE);
        })
    );
  }

  uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]) {
    const data = new FormData();
    files.forEach(persistedBlob => data.append("documentFiles", persistedBlobToISDSFormat(persistedBlob)));

    return this.callApi(api =>
      api
        .apiVersion(3)
        .one("exemptions/cancellation_applications/documents/isds")
        .postData(data, ISDSSessionId)
        .then(async response => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          return plainToClass(IsdsResponse, (await response.json()) as IsdsResponse);
        })
        .catch(() => {
          throw new EshopError(POST_OMISSION_APPLICATIONS_DOCUMENTS_ISDS_FAILURE);
        })
    );
  }
}
